import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Select, Tooltip } from 'antd';
import { customTypeNameMap, selectTypes } from '@/components/DynamicForm/utils';
import arrayMove from 'array-move';
import './CustomFieldConfigModal.less';
import {
  DeleteOutlined,
  InfoCircleOutlined,
  MenuOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons';
import { upsertCustomField } from '@/services/DynamicFormRequest';
import { DndContext } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import cx from 'classnames';
import { TChoiceItem, TCustomField } from '@/components/DynamicForm/model';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import styles from './CustomFieldConfigModal.less';

const ChoiceItem = ({
  choice,
  placeholder,
  onChange,
  choices,
}: {
  choice: TChoiceItem;
  placeholder: string;
  onChange?: (choices: TChoiceItem[]) => void;
  choices: TChoiceItem[];
}) => {
  const [repeated, setRepeated] = useState(false);
  const [empty, setEmpty] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: choice.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    const repeated =
      choice.text && choices.some((item) => choice.id !== item.id && item?.text === choice.text);
    setRepeated(repeated || false);
    setEmpty(!choice.text);
  }, [choices, choice]);

  const onValueChange = (text: string) => {
    onChange?.(
      choices.map((item) => {
        if (choice.id === item.id) {
          return {
            ...item,
            text,
            value: text,
          };
        } else {
          return item;
        }
      }),
    );
  };
  const onRemove = () => {
    const index = choices.findIndex((item) => item.id === choice.id);
    const newChoices = [...choices];
    newChoices.splice(index, 1);
    onChange?.(newChoices);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="custom-field-config-choice-config-row"
    >
      <div className="row-control">
        <Input
          value={choice.text}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={placeholder}
          className={cx('mr-[11px]', empty || repeated ? 'ant-input-status-error' : '')}
          maxLength={50}
          showCount
        />
        <div className="right-buttons">
          <MenuOutlined {...listeners} className={cx('mr-[16px]')} />
          <DeleteOutlined onClick={onRemove} />
        </div>
      </div>
      {repeated && <div className="ant-form-item-explain-error">选项内容不能重复</div>}
      {empty && <div className="ant-form-item-explain-error">选项内容不能为空</div>}
    </div>
  );
};

const ChoiceList = ({
  value,
  onChange,
  handleDragEnd,
}: {
  value?: TChoiceItem[];
  onChange?: (choices: TChoiceItem[]) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}) => {
  const onAddChoice = () => {
    if (value?.length && value.length >= 50) {
      message.warning('选项超过数量限制');
      return;
    }

    const newChoice = {
      id: (value?.length || 0) + 1,
    };
    onChange?.(value ? [...value, newChoice] : [newChoice]);
  };

  return (
    <div className="choice-config-wrapper">
      <div className={cx('text-c0c0c0', 'text-sm', 'font-normal', 'mb-[12px]', 'leading-8')}>
        请添加选项，上下拖拽可对选项排序
      </div>
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext items={value || []}>
          {value?.map((choice) => {
            return (
              <ChoiceItem
                key={choice.id}
                choices={value || []}
                onChange={onChange}
                placeholder={'请输入选项内容…'}
                choice={choice}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <div className="custom-field-config-choice-config-row">
        <div className="row-control">
          <Button type="link" onClick={onAddChoice} icon={<PlusSquareOutlined />}>
            添加选项
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function CustomFieldConfigModal({
  open,
  campId,
  customField,
  onClose,
  onSuccess,
}: {
  open: boolean;
  campId: string;
  customField?: TCustomField;
  onClose: () => void;
  onSuccess: (customField: TCustomField) => void;
}) {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [widgetTypeDisable, setWidgetTypeDisable] = useState<boolean>(false);

  useEffect(() => {
    console.log(customField);
    if (customField) {
      form.setFieldsValue({
        ...customField,
        choices: customField.choices?.map((item, index) => ({ ...item, id: index })) || [],
      });
      setWidgetTypeDisable(true);
      setShowOptions(selectTypes.includes(customField.widgetType));
    } else {
      setWidgetTypeDisable(false);
      setShowOptions(false);
    }
  }, [customField]);

  const checkFormComplete = () => {
    if (!form.getFieldValue('name') || !form.getFieldValue('widgetType')) {
      return false;
    }

    let complete = true;
    if (selectTypes.includes(form.getFieldValue('widgetType'))) {
      const items = form.getFieldValue('choices').map((item) => item?.text);
      const hasEmptyOrRepeated = items.some(
        (item, index) => !item || items.indexOf(item, index + 1) > 0,
      );
      complete = items.length > 0 && !hasEmptyOrRepeated;
    }
    return complete;
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      if (!checkFormComplete()) {
        return;
      }
      upsertCustomField({
        id: customField?.id,
        ...values,
        campId,
      }).then(() => {
        onSuccess(values);
      });
    });
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const prev = form.getFieldValue('choices');
      const oldIndex = prev.findIndex((item) => active.id === item.id);
      const newIndex = prev.findIndex((item) => over?.id === item.id);
      form.setFieldValue('choices', arrayMove(prev, oldIndex, newIndex));
    }
  }

  return (
    <Modal
      title={
        <div className="header-left">
          <div className="field-config-title">
            {!customField ? '新建自定义字段' : '编辑自定义字段'}
          </div>
          <div className="field-config-subtitle">
            {!customField ? (
              <Tooltip
                title={'新建自定义字段在当前打卡营范围内可用'}
                placement="right"
                overlayClassName="subtitle-tips"
              >
                <InfoCircleOutlined />
              </Tooltip>
            ) : null}
          </div>
        </div>
      }
      open={open}
      destroyOnClose
      onCancel={() => {
        onClose();
      }}
      okText={'确定'}
      onOk={onSubmit}
      className="custom-field-config-modal"
      wrapClassName={'vertical-adaptive'}
      width={600}
      style={{
        top: 100,
      }}
      afterClose={() => {
        form.resetFields();
      }}
    >
      <Form
        className={styles.FormWrapper}
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 18 }}
        form={form}
      >
        <Form.Item
          required
          label="字段名称"
          name="name"
          rules={[{ required: true, message: '字段名称不能为空' }]}
        >
          <Input maxLength={50} showCount />
        </Form.Item>
        <Form.Item
          required
          label="字段类型"
          name="widgetType"
          rules={[{ required: true, message: '字段类型不能为空' }]}
        >
          <Select
            disabled={widgetTypeDisable}
            listHeight={420}
            onChange={(value) => {
              if (selectTypes.includes(value)) {
                setShowOptions(true);
                if (!value.choices) {
                  form.setFieldValue('choices', [
                    { id: 1, text: '选项一', value: '选项一' },
                    { id: 2, text: '选项二', value: '选项二' },
                    { id: 3, text: '选项三', value: '选项三' },
                  ]);
                }
              } else {
                setShowOptions(false);
              }
            }}
          >
            {Object.keys(customTypeNameMap).map((key) => {
              const label = customTypeNameMap[key];
              return (
                <Select.Option value={key}>
                  <span>{label}</span>
                  {key === 'textarea' && (
                    <span style={{ color: '#7C869C', fontSize: 12, lineHeight: 0 }}>
                      （富文本）
                    </span>
                  )}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        {showOptions && (
          <Form.Item label={'选项'} name="choices">
            <ChoiceList handleDragEnd={handleDragEnd} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
